//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { crl8 } from 'app/curalate.globals';

// Import the snippet for its side effects, then use a private
// pendoLib to capture the value.
import './pendo-snippet';
import { shouldInitWithAutomatedBrowser } from '@utils/envUtils';

export class PendoTracker {
  private initCalled = false;

  // The pendo param is optional because some ad blockers
  // prevent the snippet we import above from loading. If
  // that snippet is blocked, window.pendo will be undefined
  // at runtime.
  constructor(private readonly pendo?: pendo.Pendo) {}

  public isInitialized() {
    return this.initCalled;
  }

  public init(
    release: string,
    hostname: string,
    session: ICuralateSession,
    ignoreAutomatedBrowser?: boolean,
  ) {
    if (this.isInitialized()) {
      return;
    } else if (
      shouldInitWithAutomatedBrowser(
        release,
        hostname,
        navigator.webdriver,
        ignoreAutomatedBrowser,
      )
    ) {
      this.pendo?.initialize({
        visitor: {
          id: session.user.id.toString(),
          email: session.user.email,
          full_name: `${session.user.firstName} ${session.user.lastName}`,
          isCuralateEmployee:
            session.internalInfo && session.internalInfo.isCuralateEmployee,
          portalGuid: session.user && session.user.portalGuid,
        },
        account: {
          id: session.client.id,
          name: session.client.name,
          creationDate: moment(session.client.createdTimestamp).toDate(),
          groupId: session.group.id.toString(),
          salesforceBvSiteId: session.client.salesforceBvSiteId ?? '',
        },
      });

      this.initCalled = true;
    }
  }

  public track(trackType: string, metadata?: pendo.Metadata) {
    if (this.initCalled) {
      this.pendo?.track(trackType, {
        sessionURL: crl8.logger.cloud.getCurrentSessionUrl() ?? '',
        ...metadata,
      });
    }
  }
}

const pendoTracker = new PendoTracker(pendo);
export default pendoTracker;
